import {AsyncPipe} from '@angular/common';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {catchError, map, Observable, of, tap, throwError} from 'rxjs';
import {BcbChartComponent} from './bcb-chart.component';
import {BcbRateTableComponent} from './bcb-rate-table.component';
import {BcbChartYear, BcbDataset, BcbSeriesKey} from './bcb.models';
import {FiscalRowComponent} from '../fiscal-row/fiscal-row.component';

const CACHE_KEY = 'bcb-market-dataset-v4';
const DAY = 86_400_000;

@Component({
  selector: 'app-bcb-page',
  standalone: true,
  imports: [AsyncPipe, BcbChartComponent, BcbRateTableComponent, FiscalRowComponent],
  templateUrl: './bcb-page.component.html',
  styleUrl: './bcb-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BcbPageComponent {
  private readonly http = inject(HttpClient);
  private readonly requestUrl = '/api/bcb-market';
  readonly visibleKeys = signal<ReadonlySet<BcbSeriesKey>>(
    new Set(['employment', 'cpi', 'inflationTarget', 'jobCreation', 'gold']),
  );
  readonly dashboard$: Observable<{dataset: BcbDataset; years: BcbChartYear[]}> = this.load().pipe(
    map((dataset) => ({
      dataset,
      years: Array.from({length: 10}, (_, index) => dataset.currentYear - index)
        .map((year) => ({
          year,
          series: dataset.series.map((series) => ({
            ...series,
            weeks: series.weeks.filter((week) => Number(week.lastObservation.slice(0, 4)) === year),
          })),
          policyDecisions: dataset.policyDecisions.filter(
            (decision) => Number(decision.date.slice(0, 4)) === year,
          ),
        }))
        .filter((row) => row.series.some((series) => series.weeks.length) || row.policyDecisions.length),
    })),
  );

  toggle(key: BcbSeriesKey): void {
    const next = new Set(this.visibleKeys());
    if (!next.delete(key)) next.add(key);
    this.visibleKeys.set(next);
  }

  isVisible(key: BcbSeriesKey): boolean { return this.visibleKeys().has(key); }

  private load(): Observable<BcbDataset> {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as {savedAt: number; data: BcbDataset};
        if (Date.now() - parsed.savedAt < DAY) return of(parsed.data);
      } catch { localStorage.removeItem(CACHE_KEY); }
    }
    return this.http.get<BcbDataset>(this.requestUrl, {observe: 'response'}).pipe(
      tap((response: HttpResponse<BcbDataset>) => {
        if (response.status !== 200) throw new Error(`Unexpected HTTP status ${response.status}`);
      }),
      map((response) => {
        if (!response.body) throw new Error('BCB Function returned an empty response body');
        localStorage.setItem(CACHE_KEY, JSON.stringify({savedAt: Date.now(), data: response.body}));
        return response.body;
      }),
      catchError((error: HttpErrorResponse | Error) => {
        const statusCode = error instanceof HttpErrorResponse ? error.status : 0;
        console.error('Request failed', {requestUrl: this.requestUrl, statusCode, errorMessage: error.message});
        return throwError(() => error);
      }),
    );
  }
}
